/**
 * Firebase is used here for identity and nothing else.
 *
 * Progress, attempts and exam content live in D1 behind the Worker; Firestore
 * is not used. Firebase's job is to say who is asking — through Google, or
 * through a link sent to an e-mail address. There are no anonymous accounts:
 * an account tied to one browser is a purchase and a history one wipe from
 * gone, and merging such an account into a real one later is a job nobody
 * wants to own.
 */
import { initializeApp } from "firebase/app";
import {
  GoogleAuthProvider,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  browserSessionPersistence,
  indexedDBLocalPersistence,
  initializeAuth,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  deleteUser,
  getAdditionalUserInfo,
  type User,
  type UserCredential,
} from "firebase/auth";
import { track } from "./analytics";
import { withEntry } from "./entry";
import type { LoginMethod } from "./analytics-events";

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const firebaseConfigured = Boolean(config.apiKey && config.projectId);

/**
 * `getAuth` attaches the popup/redirect resolver at once, and the resolver loads Google's
 * `apis.google.com` script and an iframe from the auth domain — about 130 KB and three
 * requests on every page view, including the prerendered pages of somebody who is not signing
 * in. So auth starts without it, with the persistence `getAuth` would have chosen, and the
 * resolver is handed to the two calls that need it. The one time it is needed at start is
 * the way back from a redirect sign-in, which is only processed by an auth that has it:
 * `signInWithGoogle` leaves a mark before leaving, and the mark is what puts it in.
 */
const REDIRECT_MARK = "telc.authRedirect";

function markRedirect(on: boolean): void {
  try {
    if (on) localStorage.setItem(REDIRECT_MARK, "1");
    else localStorage.removeItem(REDIRECT_MARK);
  } catch {
    /* private mode: a redirect there would not be remembered either way */
  }
}

function returningFromRedirect(): boolean {
  try {
    return localStorage.getItem(REDIRECT_MARK) !== null;
  } catch {
    return false;
  }
}

const app = firebaseConfigured ? initializeApp(config) : null;
export const auth = app
  ? initializeAuth(app, {
      persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
      popupRedirectResolver: returningFromRedirect() ? browserPopupRedirectResolver : undefined,
    })
  : null;
// The redirect, if there was one, has been read by now.
void auth?.authStateReady().then(() => markRedirect(false));

function required(): NonNullable<typeof auth> {
  if (!auth) throw new Error("Firebase nie jest skonfigurowany.");
  return auth;
}

/** A fresh token for the Authorization header; Firebase refreshes it itself. */
export async function idToken(): Promise<string> {
  const current = required().currentUser;
  if (!current) throw new Error("Nikt nie jest zalogowany.");
  return current.getIdToken();
}

/* ------------------------------------------------------------------ */
/* Google                                                              */
/* ------------------------------------------------------------------ */

/**
 * A popup where one is possible, a redirect where it is not.
 *
 * Embedded browsers — the in-app views of Instagram, Facebook and friends,
 * where a fair share of this traffic arrives — block popups outright, and a
 * blocked popup looks to the user like a button that does nothing. The
 * redirect leaves the page and comes back signed in; `onAuthStateChanged`
 * picks it up on the way in, so there is nothing to await here.
 *
 * Returns false when the user simply closed the popup: not an error, and
 * nothing to say about it.
 */
export async function signInWithGoogle(): Promise<boolean> {
  const instance = required();
  const provider = new GoogleAuthProvider();
  // Without this, a second sign-in silently reuses the one Google session the
  // browser remembers, which is wrong for a shared computer.
  provider.setCustomParameters({ prompt: "select_account" });

  track("login_start", { method: "google" });
  try {
    signedIn("google", await signInWithPopup(instance, provider, browserPopupRedirectResolver));
    return true;
  } catch (err) {
    const code = (err as { code?: string }).code ?? "";
    if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request")
      return false;
    if (
      code === "auth/popup-blocked" ||
      code === "auth/operation-not-supported-in-this-environment"
    ) {
      // The page leaves for Google and comes back fresh; nothing on the way
      // back says it was a sign-in, so this path reports only its start.
      // Firebase returns to the address the page has now, so the entry rides
      // in it (`entry.ts`): the tab's memory does not survive the trip.
      window.history.replaceState(window.history.state, "", withEntry(window.location.href));
      markRedirect(true);
      await signInWithRedirect(instance, provider, browserPopupRedirectResolver);
      return true;
    }
    track("login_error", { method: "google", error_code: code || "unknown" });
    throw err;
  }
}

/** `sign_up` the first time an account signs in, `login` every time after. */
function signedIn(method: LoginMethod, credential: UserCredential): void {
  track(getAdditionalUserInfo(credential)?.isNewUser ? "sign_up" : "login", { method });
}

/* ------------------------------------------------------------------ */
/* Passwordless e-mail sign-in                                         */
/* ------------------------------------------------------------------ */

/**
 * Where the e-mail link brings the user back to. It has to be listed under
 * Firebase -> Authentication -> Settings -> Authorized domains, or the link
 * is refused on arrival.
 *
 * Deliberately no `#`: Firebase appends its own `oobCode` and friends as query
 * parameters, and appending a query after a fragment buries them inside the
 * fragment, where `isSignInWithEmailLink` cannot see them. The app spots the
 * link on whichever route it lands on and finishes the job there.
 */
const RETURN_URL = (): string => withEntry(`${window.location.origin}/`);

/** The address is kept so the returning link does not have to ask for it again. */
const PENDING_EMAIL_KEY = "telc.pendingEmail";

export async function sendEmailLink(email: string): Promise<void> {
  track("login_start", { method: "email_link" });
  try {
    await sendSignInLinkToEmail(required(), email, {
      // The link may be opened hours later or on another device; the entry
      // that led here travels in it (`entry.ts`).
      url: RETURN_URL(),
      handleCodeInApp: true,
    });
  } catch (err) {
    const code = (err as { code?: string }).code ?? "unknown";
    track("login_error", { method: "email_link", error_code: code });
    throw err;
  }
  track("email_link_sent");
  try {
    localStorage.setItem(PENDING_EMAIL_KEY, email);
  } catch {
    /* private mode: the returning link will ask for the address instead */
  }
}

export const pendingEmail = (): string | null => {
  try {
    return localStorage.getItem(PENDING_EMAIL_KEY);
  } catch {
    return null;
  }
};

const clearPendingEmail = (): void => {
  try {
    localStorage.removeItem(PENDING_EMAIL_KEY);
  } catch {
    /* nothing to clean up */
  }
};

export const isEmailLink = (url: string): boolean =>
  auth !== null && isSignInWithEmailLink(auth, url);

/** Completes sign-in from an e-mail link. The address is its own account. */
export async function completeEmailLink(email: string, url: string): Promise<void> {
  try {
    signedIn("email_link", await signInWithEmailLink(required(), email, url));
  } catch (err) {
    const code = (err as { code?: string }).code ?? "unknown";
    track("login_error", { method: "email_link", error_code: code });
    // Clarity's recordings show the screen but not why; a tag makes the
    // failed sign-ins filterable there. Only exists once consent loaded it.
    (window as typeof window & { clarity?: (...a: unknown[]) => void }).clarity?.(
      "set",
      "login_error",
      code,
    );
    throw err;
  }
  clearPendingEmail();
}

export async function signOutUser(): Promise<void> {
  track("logout");
  if (auth) await signOut(auth);
}

/**
 * Removes the Firebase user itself, after the server has forgotten them.
 *
 * Firebase refuses this for a session that signed in long ago, and there is
 * nothing useful to do about it here: the data is already gone, so we sign out
 * and let the empty account expire on Firebase's side.
 */
export async function deleteFirebaseUser(): Promise<void> {
  const current = auth?.currentUser;
  if (!current) return;
  try {
    await deleteUser(current);
  } catch {
    await signOutUser();
  }
}

export type { User };
